import { useEffect, useMemo, useState } from "react";
import { doc, getDoc, serverTimestamp, updateDoc } from "../access/firestore";
import { db } from "../firebase";

const desktopSteps = [
  {
    target: "settings",
    section: "settings",
    permission: "configuracion",
    eyebrow: "Primer paso",
    title: "Configura tu consultorio",
    body: "Haz clic aquí para ingresar el nombre, datos de contacto, horarios y la identidad visual de tu consultorio.",
  },
  {
    target: "pacientes",
    section: "pacientes",
    permission: "pacientes",
    eyebrow: "Tu base clínica",
    title: "Registra a tus pacientes",
    body: "Desde Pacientes puedes crear expedientes, guardar fotografías, tratamientos, documentos y toda la historia clínica.",
  },
  {
    target: "citas",
    section: "citas",
    permission: "citas",
    eyebrow: "Tu agenda",
    title: "Organiza tus citas",
    body: "Programa consultas, revisa la agenda del equipo y conecta tus citas con Google Calendar cuando estés listo.",
  },
  {
    target: "whatsapp",
    section: "whatsapp",
    permission: "whatsapp",
    eyebrow: "Comunicación",
    title: "Conecta con tus pacientes",
    body: "En WhatsApp puedes responder conversaciones, enviar archivos y automatizar recordatorios de citas.",
  },
  {
    target: "finanzas",
    section: "finanzas",
    permission: "finanzas",
    eyebrow: "Control del consultorio",
    title: "Administra tus finanzas",
    body: "Registra pagos, consulta saldos pendientes y genera recibos desde un solo lugar.",
  },
  {
    target: "usuarios",
    section: "usuarios",
    permission: "usuarios",
    eyebrow: "Trabajo en equipo",
    title: "Invita a tu equipo",
    body: "Agrega usuarios y define qué módulos puede consultar o administrar cada integrante.",
  },
  {
    target: "dashboard",
    section: "dashboard",
    permission: "dashboard",
    eyebrow: "Tu punto de partida",
    title: "Este es tu Panel General",
    body: "Aquí encontrarás un resumen de citas, ingresos, pacientes y pendientes para comenzar cada día con claridad.",
  },
];

const mobileSteps = [
  {
    target: "mobile-more",
    section: "mas",
    permission: "configuracion",
    eyebrow: "Primer paso",
    title: "Configura tu consultorio",
    body: "Toca Más y después Ajustes para ingresar el nombre, horarios y datos de tu consultorio.",
  },
  {
    target: "mobile-patients",
    section: "pacientes",
    permission: "pacientes",
    eyebrow: "Tu base clínica",
    title: "Registra a tus pacientes",
    body: "Desde Pacientes puedes crear expedientes y consultar la información clínica desde cualquier lugar.",
  },
  {
    target: "mobile-appointments",
    section: "citas",
    permission: "citas",
    eyebrow: "Tu agenda",
    title: "Organiza tus citas",
    body: "Toca Citas para revisar tu agenda y mantener el día bajo control.",
  },
  {
    target: "mobile-whatsapp",
    section: "whatsapp",
    permission: "whatsapp",
    eyebrow: "Comunicación",
    title: "Conecta con tus pacientes",
    body: "Desde WhatsApp puedes responder mensajes, enviar archivos y dar seguimiento a tus conversaciones.",
  },
  {
    target: "mobile-more",
    section: "mas",
    permission: "finanzas",
    eyebrow: "Control del consultorio",
    title: "Encuentra Finanzas",
    body: "En Más también encontrarás Finanzas para registrar pagos, saldos y recibos.",
  },
  {
    target: "mobile-more",
    section: "mas",
    permission: "usuarios",
    eyebrow: "Trabajo en equipo",
    title: "Invita a tu equipo",
    body: "En Más > Usuarios puedes invitar integrantes y definir sus permisos.",
  },
  {
    target: "mobile-home",
    section: "inicio",
    permission: "dashboard",
    eyebrow: "Tu punto de partida",
    title: "Vuelve a Inicio cuando quieras",
    body: "Inicio te muestra un resumen de lo más importante de tu consultorio para comenzar el día.",
  },
];

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export default function OnboardingTour({
  user,
  clinicId,
  userData,
  isMobile = false,
  currentSection,
  onNavigate,
}) {
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const steps = useMemo(() => {
    const permissions = userData?.permisos || {};
    const source = isMobile ? mobileSteps : desktopSteps;
    return source.filter(step => permissions[step.permission] !== false);
  }, [isMobile, userData?.permisos]);

  const storageKey = user?.uid && clinicId
    ? `vexadent:onboarding:v1:${user.uid}:${clinicId}`
    : null;

  useEffect(() => {
    let cancelled = false;
    setOpen(false);
    if (!storageKey || !steps.length || !user?.uid) return undefined;

    const loadTutorialState = async () => {
      let localState = null;
      try {
        localState = window.localStorage.getItem(storageKey);
      } catch (error) {
        console.warn("No se pudo leer el estado local del tutorial", error);
      }

      try {
        const snapshot = await getDoc(doc(db, "users", user.uid));
        if (cancelled) return;
        const remoteState = snapshot.data()?.onboardingTutorialV1;
        if (remoteState === "completed" || remoteState === "skipped") {
          setOpen(false);
          try {
            window.localStorage.setItem(storageKey, remoteState);
          } catch (error) {
            console.warn("No se pudo actualizar el estado local del tutorial", error);
          }
          return;
        }

        if (localState === "completed" || localState === "skipped") {
          setOpen(false);
          await updateDoc(doc(db, "users", user.uid), {
            onboardingTutorialV1: localState,
            onboardingTutorialV1UpdatedAt: serverTimestamp(),
          });
          return;
        }

        setOpen(true);
      } catch (error) {
        console.warn("No se pudo sincronizar el estado del tutorial", error);
        if (!cancelled) setOpen(localState !== "completed" && localState !== "skipped");
      }
    };

    loadTutorialState();
    return () => {
      cancelled = true;
    };
  }, [storageKey, steps.length, user?.uid]);

  useEffect(() => {
    if (stepIndex >= steps.length) setStepIndex(0);
  }, [stepIndex, steps.length]);

  useEffect(() => {
    if (!open || !steps.length) return;
    const step = steps[stepIndex];
    if (step?.section && currentSection !== step.section) onNavigate?.(step.section);
  }, [currentSection, onNavigate, open, stepIndex, steps]);

  useEffect(() => {
    if (!open || !steps.length) return undefined;

    let frame = 0;
    const measure = () => {
      const step = steps[stepIndex];
      const element = step ? document.querySelector(`[data-tour="${step.target}"]`) : null;
      if (!element) {
        setTargetRect(null);
        return;
      }
      const rect = element.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height,
      });
    };

    frame = window.requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, stepIndex, steps, currentSection]);

  if (!open || !steps.length || !storageKey) return null;

  const step = steps[stepIndex];
  const lastStep = stepIndex === steps.length - 1;
  const padding = 8;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const cardWidth = Math.min(370, viewportWidth - 32);
  const cardHeight = 250;
  const target = targetRect || {
    top: viewportHeight / 2 - 28,
    left: viewportWidth / 2 - 28,
    right: viewportWidth / 2 + 28,
    bottom: viewportHeight / 2 + 28,
    width: 56,
    height: 56,
  };
  const showBelow = target.bottom + cardHeight + 24 <= viewportHeight;
  const cardTop = showBelow
    ? target.bottom + 18
    : clamp(target.top - cardHeight - 18, 16, viewportHeight - cardHeight - 16);
  const cardLeft = clamp(
      target.left + target.width / 2 - cardWidth / 2,
      16,
      viewportWidth - cardWidth - 16,
  );

  const finish = async result => {
    try {
      window.localStorage.setItem(storageKey, result);
    } catch (error) {
      console.warn("No se pudo guardar el estado del tutorial", error);
    }
    setOpen(false);
    try {
      await updateDoc(doc(db, "users", user.uid), {
        onboardingTutorialV1: result,
        onboardingTutorialV1UpdatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.warn("No se pudo guardar el tutorial en Firestore", error);
    }
  };

  const next = () => {
    if (lastStep) {
      finish("completed");
      return;
    }
    setStepIndex(value => value + 1);
  };

  return (
    <div className="vd-onboarding-tour" role="dialog" aria-modal="true" aria-label="Tutorial de VexaDent">
      {targetRect ? (
        <>
          <div style={{position:"fixed",zIndex:19990,top:0,left:0,right:0,height:Math.max(target.top - padding, 0),pointerEvents:"none",background:"rgba(9,22,42,0.62)"}} />
          <div style={{position:"fixed",zIndex:19990,top:Math.max(target.top - padding, 0),left:0,width:Math.max(target.left - padding, 0),height:target.height + padding * 2,pointerEvents:"none",background:"rgba(9,22,42,0.62)"}} />
          <div style={{position:"fixed",zIndex:19990,top:Math.max(target.top - padding, 0),left:target.right + padding,right:0,height:target.height + padding * 2,pointerEvents:"none",background:"rgba(9,22,42,0.62)"}} />
          <div style={{position:"fixed",zIndex:19990,top:target.bottom + padding,left:0,right:0,bottom:0,pointerEvents:"none",background:"rgba(9,22,42,0.62)"}} />
        </>
      ) : (
        <div style={{position:"fixed",inset:0,zIndex:19990,pointerEvents:"none",background:"rgba(9,22,42,0.62)"}} />
      )}
      {targetRect && (
        <div
          style={{
            position:"fixed",
            zIndex:19991,
            top:target.top - padding,
            left:target.left - padding,
            width:target.width + padding * 2,
            height:target.height + padding * 2,
            borderRadius:14,
            border:"2px solid #5eead4",
            boxShadow:"0 0 0 4px rgba(94,234,212,.18), 0 16px 35px rgba(15,23,42,.24)",
            pointerEvents:"none",
          }}
        />
      )}
      <section
        style={{
          position:"fixed",
          zIndex:19992,
          top:cardTop,
          left:cardLeft,
          width:cardWidth,
          boxSizing:"border-box",
          padding:"22px 22px 18px",
          borderRadius:22,
          background:"linear-gradient(145deg,#ffffff 0%,#f5fbff 100%)",
          border:"1px solid rgba(148,163,184,.25)",
          boxShadow:"0 24px 70px rgba(3,14,35,.28)",
          color:"#102a43",
          fontFamily:"Inter,system-ui,Arial",
        }}
      >
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:16,marginBottom:15}}>
          <span style={{fontSize:11,fontWeight:800,letterSpacing:".14em",textTransform:"uppercase",color:"#0f9f9a"}}>{step.eyebrow}</span>
          <span style={{fontSize:12,fontWeight:700,color:"#64748b"}}>{stepIndex + 1} de {steps.length}</span>
        </div>
        <div style={{height:5,borderRadius:99,background:"#dbeafe",overflow:"hidden",marginBottom:18}}>
          <div style={{height:"100%",width:`${((stepIndex + 1) / steps.length) * 100}%`,borderRadius:99,background:"linear-gradient(90deg,#14b8a6,#2563eb)",transition:"width 220ms ease"}} />
        </div>
        <h2 style={{margin:"0 0 9px",fontSize:23,lineHeight:1.15,letterSpacing:"-.025em"}}>{step.title}</h2>
        <p style={{margin:"0 0 20px",fontSize:14,lineHeight:1.55,color:"#52657a"}}>{step.body}</p>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
          <button type="button" onClick={() => finish("skipped")} style={{border:0,background:"transparent",padding:"8px 0",color:"#64748b",fontWeight:700,cursor:"pointer"}}>Omitir tutorial</button>
          <div style={{display:"flex",gap:8}}>
            {stepIndex > 0 && <button type="button" onClick={() => setStepIndex(value => value - 1)} style={{border:"1px solid #cbd5e1",background:"#fff",borderRadius:11,padding:"10px 13px",color:"#334155",fontWeight:700,cursor:"pointer"}}>Atrás</button>}
            <button type="button" onClick={next} style={{border:0,background:"linear-gradient(135deg,#2563eb,#14b8a6)",borderRadius:11,padding:"10px 15px",color:"#fff",fontWeight:800,cursor:"pointer"}}>{lastStep ? "Comenzar a usar VexaDent" : "Siguiente"}</button>
          </div>
        </div>
      </section>
    </div>
  );
}
